var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { cx, css as emotionCss } from 'emotion';
import { defaultOnNull, first, get, isNotNilOrEmpty, isNilOrEmpty } from '@pcty/utils';
import { BannerWrapper, BoundingBox1440, BoundingBox1280, BoundingBox960, OverviewWrapper, OverviewContentWrapper, SearchWrapper, SearchWrapperSideBar, SideBarLeftWrapper, SideBarRightWrapper, SideBarWrapper, SideBarContentWrapper, FilterWrapper, FilterWrapperSideBar, Wrapper, } from './Layout.styled';
import { header as headerCss } from '../styles/legacy';
import { getAutomation, getDebug, getStyleNames } from '@citrus/runtime';
import { useTheme } from '@citrus/user';
import Tabs from './LayoutTabs';
import LayoutLegacy from './LayoutLegacy';
import { GridItem } from './GridItem';
var VERSION = '6.7.2';
var Layout = React.forwardRef(function (_a, ref) {
    var _b;
    var automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, margin = _a.margin, _c = _a.maxWidth, maxWidth = _c === void 0 ? 1400 : _c, _d = _a.noBoundingBox, noBoundingBox = _d === void 0 ? false : _d, _e = _a.noContentHeaderMargin, noContentHeaderMargin = _e === void 0 ? false : _e, _f = _a.padding, padding = _f === void 0 ? true : _f, renderBanner = _a.renderBanner, renderFooter = _a.renderFooter, renderHeader = _a.renderHeader, renderSearch = _a.renderSearch, renderOverview = _a.renderOverview, _g = _a.sideBarPosition, sideBarPosition = _g === void 0 ? 'left' : _g, _h = _a.__TYPE, TYPE = _h === void 0 ? 'Layout' : _h, other = __rest(_a, ["automationId", "children", "className", "css", "margin", "maxWidth", "noBoundingBox", "noContentHeaderMargin", "padding", "renderBanner", "renderFooter", "renderHeader", "renderSearch", "renderOverview", "sideBarPosition", "__TYPE"]);
    var legacy = useTheme().legacy;
    if (legacy)
        return (React.createElement(LayoutLegacy, __assign({ automationId: automationId, className: className, css: css, maxWidth: maxWidth, noBoundingBox: noBoundingBox, noContentHeaderMargin: noContentHeaderMargin, padding: padding, renderBanner: renderBanner, renderFooter: renderFooter, renderHeader: renderHeader, renderSearch: renderSearch, renderOverview: renderOverview, sideBarPosition: sideBarPosition }, other), children));
    var banner;
    var overview;
    var sidebar = renderComponentByType(['SideBar'], children);
    var content = renderComponentByType(['Content'], children);
    banner = renderBanner ? renderBanner() : undefined;
    overview = getOverview(renderOverview);
    var search = getSearch(renderSearch ? renderSearch() : undefined, isNotNilOrEmpty(sidebar));
    if (isNotNilOrEmpty(banner) && isNilOrEmpty(sidebar) && isNilOrEmpty(content)) {
        banner = React.createElement(BannerWrapper, null, banner);
    }
    if (isNotNilOrEmpty(overview) && isNilOrEmpty(sidebar) && isNilOrEmpty(content)) {
        overview = React.createElement(OverviewWrapper, null, overview);
    }
    if (isNotNilOrEmpty(banner) || isNotNilOrEmpty(search) || (isNotNilOrEmpty(sidebar) && isNotNilOrEmpty(content))) {
        noContentHeaderMargin = true;
    }
    var footer = getFooter(renderFooter);
    var header = getHeader(renderHeader, noContentHeaderMargin);
    children = getChildren(header, footer, sidebar, content, banner, overview, margin, padding, search, sideBarPosition, children);
    var emotionObj = (_b = {},
        _b[emotionCss(headerCss)] = isNotNilOrEmpty(header),
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        headerCss: emotionObj[emotionCss(headerCss)],
    };
    var renderWrapper = function () { return (React.createElement(Wrapper, __assign({ className: classes, ref: ref, styleNames: getStyleNames(styleNames) }, other, getAutomation(automationId), getDebug(sessionStorage.getItem('citrus-debug') === 'true', TYPE, VERSION)),
        isNilOrEmpty(sidebar) ? header : undefined,
        isNilOrEmpty(sidebar) ? search : undefined,
        isNilOrEmpty(sidebar) ? banner : undefined,
        isNilOrEmpty(sidebar) ? overview : undefined,
        children,
        isNilOrEmpty(sidebar) ? footer : undefined)); };
    if (maxWidth === '100%' || noBoundingBox) {
        return renderWrapper();
    }
    var BoundingBox = getBoundingBox(maxWidth);
    return React.createElement(BoundingBox, null, renderWrapper());
});
var getSearch = function (search, sidebar) {
    if (isNotNilOrEmpty(search)) {
        if (get(search, 'props.__TYPE') === 'Filter' || get(search, 'props.__TYPE') === 'FilterSaved') {
            return sidebar ? React.createElement(FilterWrapperSideBar, null, search) : React.createElement(FilterWrapper, null, search);
        }
        return sidebar ? React.createElement(SearchWrapperSideBar, null, search) : React.createElement(SearchWrapper, null, search);
    }
};
var getFooter = function (renderFooter) {
    if (!renderFooter)
        return;
    var footer = renderFooter();
    if (isNilOrEmpty(footer))
        return;
    return React.cloneElement(footer);
};
var getHeader = function (renderHeader, noContentHeaderMargin) {
    if (!renderHeader)
        return;
    var header = renderHeader();
    if (isNilOrEmpty(header))
        return;
    var css = __assign(__assign({}, defaultOnNull(header.props.css, {})), (noContentHeaderMargin ? {} : headerCss));
    return React.cloneElement(header, { pageHeader: false, css: css });
};
var getChildren = function (header, footer, sidebar, content, banner, overview, margin, padding, search, sideBarPosition, children) {
    if (isNotNilOrEmpty(sidebar) && isNotNilOrEmpty(content) && sideBarPosition === 'left') {
        return (React.createElement(SideBarLeftWrapper, null,
            React.createElement(GridItem, { column: '1 / -1' }, header),
            sidebar,
            React.createElement(SideBarContentWrapper, null,
                search,
                banner,
                isNotNilOrEmpty(overview) && React.createElement(OverviewContentWrapper, null, overview),
                content),
            React.createElement(GridItem, { column: '1 / -1' }, footer)));
    }
    else if (isNotNilOrEmpty(sidebar) && isNotNilOrEmpty(content) && sideBarPosition === 'right') {
        return (React.createElement(SideBarRightWrapper, null,
            React.createElement(GridItem, { column: '1 / -1' }, header),
            React.createElement("div", { style: { minHeight: 500 } },
                search,
                banner,
                isNotNilOrEmpty(overview) && React.createElement(OverviewContentWrapper, null, overview),
                content),
            React.createElement(SideBarWrapper, null, sidebar),
            React.createElement(GridItem, { column: '1 / -1' }, footer)));
    }
    if (!margin && !padding)
        return children;
    return (React.createElement("div", { style: { margin: margin ? '32px 0' : undefined, padding: padding ? '0 32px' : undefined } }, children));
};
var renderComponentByType = function (childTypes, children) {
    return first(React.Children.toArray(children).filter(function (child) { return child && child.props && childTypes.indexOf(child.props.__TYPE) >= 0; }));
};
var getOverview = function (renderOverview) {
    if (!renderOverview)
        return;
    var overview = renderOverview();
    if (isNilOrEmpty(overview))
        return;
    return React.cloneElement(overview);
};
var getBoundingBox = function (maxWidth) {
    switch (maxWidth) {
        case 1400:
            return BoundingBox1440;
        case 1280:
        default:
            return BoundingBox1280;
        case 960:
            return BoundingBox960;
    }
};
Layout.defaultProps = {
    __TYPE: 'Layout',
};
export default Object.assign(Layout, { Tabs: Tabs });
